import React, { useState, useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import OriginTracing from '@atlassiansox/origin-tracing';

import Button, { LoadingButton } from '@atlaskit/button';
import InlineDialog from '@atlaskit/inline-dialog';
import { useModal } from '@atlaskit/modal-dialog';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import { N400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data';
import { getApolloClient } from '@confluence/graphql';
import { ConfluenceEdition } from '@confluence/change-edition';

import spotHeroIllustration from './assets/spot-hero-illustrations.svg';
import spotHeroIllustrationDark from './assets/dark-spot-hero-illustrations.svg';
import {
	ADMIN_HUB_NON_PROD_URL,
	ADMIN_HUB_PROD_URL,
	LEARN_MORE_URL,
	LEARN_SWITCH_URL,
	REFERRER,
	USER_MANAGEMENT_URL,
} from './constants';
import type { LockScreenContentProps } from './types';
import {
	ContentWrapper,
	BodyText,
	CustomBody,
	CustomHeader,
	BodyHeading,
	ContentContainer,
	SiteAdminSection,
	InlineDialogContentWrapper,
	SiteAdminList,
	BodyListFrame,
	BodyListItem,
	BodyListTitle,
	BodyListWrapper,
	ButtonContainer,
	BodyLink,
} from './styled';
import { UserInfo } from './UserInfo.graphql';
import { fetchSiteAdmins } from './FetchSiteAdmins';
import { useAnalytics } from './Analytics';

const i18n = defineMessages({
	belowFreeUserLimitTitleStandard: {
		id: 'experiment-lock-screen.below-free-user-limit-title-standard',
		defaultMessage: 'Your 14-day trial of the Standard plan has ended',
		description:
			'Title for the lock screen informing user that the free trial has ended for standard edition',
	},
	belowFreeUserLimitTitlePremium: {
		id: 'experiment-lock-screen.below-free-user-limit-title-premium',
		defaultMessage: 'Your 30-day trial of the Premium plan has ended',
		description:
			'Title for the lock screen informing user that the free trial has ended for premium edition',
	},
	aboveFreeUserLimitTitle: {
		id: 'experiment-lock-screen.above-free-user-limit-title',
		defaultMessage:
			'Confluence will be deactivated in {predunningDaysLeft} {predunningDaysLeft, plural, one {day} other {days}}',
		description:
			'Title for the lock screen showing that the free trial has ended and the instance will be deactivated',
	},
	adminBelowFreeUserLimitInstructionStandard: {
		id: 'experiment-lock-screen.admin-below-free-user-limit-instruction-standard',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"You've reached the end of your trial and not provided payment details. Add your payment details to stay on Standard, or switch to the Free plan.",
		description:
			'Body text for the lock screen showing possible next steps when number of user is below limit for standard edition',
	},
	adminBelowFreeUserLimitInstructionPremium: {
		id: 'experiment-lock-screen.admin-below-free-user-limit-instruction-premium',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"You've reached the end of your trial and not provided payment details. Add your payment details to stay on Premium, or switch to the Free plan.",
		description:
			'Body text for the lock screen showing possible next steps when number of user is below limit for premium edition',
	},
	adminAboveFreeUserLimitInstructionStandard: {
		id: 'experiment-lock-screen.admin-above-free-user-limit-instruction-standard',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"You've reached the end of your trial and not provided payment details. To avoid losing your current data and setup, add your payment details to stay on Standard.{br}" +
			'Or <link>learn how to switch to Free.</link>',
		description:
			'Body text for the lock screen showing possible next steps when number of user is above limit for standard edition',
	},
	adminAboveFreeUserLimitInstructionPremium: {
		id: 'experiment-lock-screen.admin-above-free-user-limit-instruction-premium',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"You've reached the end of your trial and not provided payment details. To avoid losing your current data and setup, add your payment details to stay on Premium.{br}" +
			'Or <link>learn how to switch to Free.</link>',
		description:
			'Body text for the lock screen showing possible next steps when number of user is above limit for premium edition',
	},
	billingAdminBenefitTitleStandard: {
		id: 'experiment-lock-screen.billing-admin-benefit-title-standard',
		defaultMessage: 'Add payment details to stay on Standard and keep:',
		description: 'Title for the benefit section for billing admin for standard edition',
	},
	billingAdminBenefitTitlePremium: {
		id: 'experiment-lock-screen.billing-admin-benefit-title-premium',
		defaultMessage: 'Add payment details to stay on Premium and keep:',
		description: 'Title for the benefit section for billing admin for premium edition',
	},
	nonBillingAdminBenefitTitleStandard: {
		id: 'experiment-lock-screen.non-billing-admin-benefit-title-standard',
		defaultMessage: 'Stay on Standard and keep:',
		description: 'Title for the benefit section for non-billing admin for standard edition',
	},
	nonBillingAdminBenefitTitlePremium: {
		id: 'experiment-lock-screen.non-billing-admin-benefit-title-premium',
		defaultMessage: 'Stay on Premium and keep:',
		description: 'Title for the benefit section for non-billing admin for premium edition',
	},
	contactBillingAdminStandard: {
		id: 'experiment-lock-screen.contact-billing-admin-standard',
		defaultMessage: 'Contact your admin to request the full Standard plan for your team.',
		description:
			'Footer text instructing user to contact billing admin if they want to keep standard plan',
	},
	contactBillingAdminPremium: {
		id: 'experiment-lock-screen.contact-billing-admin-premium',
		defaultMessage: 'Contact your admin to request the full Premium plan for your team.',
		description:
			'Footer text instructing user to contact billing admin if they want to keep premium plan',
	},
	viewAdmins: {
		id: 'experiment-lock-screen.view-admins',
		defaultMessage: 'View admins',
		description: 'Button label for viewing site admins',
	},
	siteAdmins: {
		id: 'experiment-lock-screen.site-admins',
		defaultMessage: 'Site admins',
		description: 'Dialog header for viewing site admins',
	},
	failedToLoadSiteAdmins: {
		id: 'experiment-lock-screen.failed-to-load-site-admins',
		defaultMessage: 'Failed to load data. Try again in a few moments.',
		description: 'Error message to display when there is issue fetching site admins',
	},
	accessAllUsers: {
		id: 'experiment-lock-screen.access-all-users',
		defaultMessage: 'Access for all of your users',
		description: 'Text to show one of the benefit of keeping standard is access for all users',
	},
	businessHourSupport: {
		id: 'experiment-lock-screen.business-hour-support',
		defaultMessage: 'Business hour support',
		description:
			'Text to show one of the benefit of keeping standard is support during business hours',
	},
	spaceAndPagePermissions: {
		id: 'experiment-lock-screen.space-and-page-permissions',
		defaultMessage: 'Space and page permissions',
		description:
			'Text to show one of the benefit of keeping standard is space and page permissions',
	},
	teamCalendar: {
		id: 'experiment-lock-screen.team-calendar',
		defaultMessage: 'Team calendar',
		description: 'Text to show one of the benefit of keeping premium is team calendar',
	},
	analytics: {
		id: 'experiment-lock-screen.analytics',
		defaultMessage: 'Analytics',
		description: 'Text to show one of the benefit of keeping premium is analytics',
	},
	advancedPermissions: {
		id: 'experiment-lock-screen.advanced-permissions',
		defaultMessage: 'Advanced permissions',
		description: 'Text to show one of the benefit of keeping premium is advanced permissions',
	},
	learnMoreUrlTextStandard: {
		id: 'experiment-lock-screen.learn-more-url-text-standard',
		defaultMessage: 'Learn more about the benefits of the Standard plan',
		description: 'Text for Url link to read more about benefits of the standard plan',
	},
	learnMoreUrlTextPremium: {
		id: 'experiment-lock-screen.learn-more-url-text-premium',
		defaultMessage: 'Learn more about the benefits of the Premium plan',
		description: 'Text for Url link to read more about benefits of the premium plan',
	},
	addPaymentDetails: {
		id: 'experiment-lock-screen.add-payment-details',
		defaultMessage: 'Add payment details',
		description: 'Button label for adding payment details ',
	},
	switchToFree: {
		id: 'experiment-lock-screen.switch-to-free',
		defaultMessage: 'Switch to Free',
		description: 'Button label for switching to free plan',
	},
	removeUsersAndSwitchToFree: {
		id: 'experiment-lock-screen.remove-users-and-switch-to-free',
		defaultMessage: 'Remove users and switch to Free',
		description: 'Button label for removing users and switching to free plan',
	},
	dismiss: {
		id: 'experiment-lock-screen.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Button label to dismiss the lock screen',
	},
});

const getContentModel = (edition: ConfluenceEdition | null) => {
	if (edition === ConfluenceEdition.PREMIUM) {
		return {
			belowFreeUserLimitTitle: i18n.belowFreeUserLimitTitlePremium,
			adminBelowFreeUserLimitInstruction: i18n.adminBelowFreeUserLimitInstructionPremium,
			adminAboveFreeUserLimitInstruction: i18n.adminAboveFreeUserLimitInstructionPremium,
			billingAdminBenefitTitle: i18n.billingAdminBenefitTitlePremium,
			nonBillingAdminBenefitTitle: i18n.nonBillingAdminBenefitTitlePremium,
			contactBillingAdmin: i18n.contactBillingAdminPremium,
			learnMoreUrlText: i18n.learnMoreUrlTextPremium,
			benefits: [i18n.teamCalendar, i18n.analytics, i18n.advancedPermissions],
		};
	}

	return {
		belowFreeUserLimitTitle: i18n.belowFreeUserLimitTitleStandard,
		adminBelowFreeUserLimitInstruction: i18n.adminBelowFreeUserLimitInstructionStandard,
		adminAboveFreeUserLimitInstruction: i18n.adminAboveFreeUserLimitInstructionStandard,
		billingAdminBenefitTitle: i18n.billingAdminBenefitTitleStandard,
		nonBillingAdminBenefitTitle: i18n.nonBillingAdminBenefitTitleStandard,
		contactBillingAdmin: i18n.contactBillingAdminStandard,
		learnMoreUrlText: i18n.learnMoreUrlTextStandard,
		benefits: [i18n.accessAllUsers, i18n.businessHourSupport, i18n.spaceAndPagePermissions],
	};
};

export const LockScreenContent = ({
	isBillingUser,
	isBillingAdmin,
	usageOverLimit,
	transactionAccountId,
	invoiceGroupId,
	entitlementId,
	predunningDaysLeft,
	isDarkMode,
	isDismissible,
	onDismiss,
	titleId,
	originTracing = new OriginTracing({ product: 'confluence' }),
}: LockScreenContentProps) => {
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
	const [siteAdmins, setSiteAdmins] = useState<{ id: string; displayName: string }[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isErrorFetchingAdmins, setIsErrorFetchingAdmins] = useState<boolean>(false);
	const { fireAnalytics } = useAnalytics();
	const { cloudId, environment, edition } = useSessionData();

	const apolloClient = getApolloClient();
	const adminHubUrl = environment === 'PRODUCTION' ? ADMIN_HUB_PROD_URL : ADMIN_HUB_NON_PROD_URL;

	const learnSwitchUrl = originTracing.addToUrl(LEARN_SWITCH_URL);
	const learnMoreUrl = originTracing.addToUrl(LEARN_MORE_URL);
	const userManagementUrl = originTracing.addToUrl(USER_MANAGEMENT_URL);
	const manageSubscriptionUrl = originTracing.addToUrl(
		`${adminHubUrl}/billing/${transactionAccountId}/entitlement/${entitlementId}`,
	);

	const paymentDetailsUrl = originTracing.addToUrl(
		`${adminHubUrl}/billing/${transactionAccountId}/${invoiceGroupId}/payment-flow?referrer=${REFERRER}`,
	);

	const isAdmin = isBillingAdmin || isBillingUser;

	const contentModel = getContentModel(edition);

	const handleClickLearnSwitch = useCallback(() => {
		fireAnalytics({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'lockScreenLearnSwitchFreeLink',
			attributes: {
				...originTracing.toAnalyticsAttributes({ hasGeneratedId: true }),
			},
		});
	}, [fireAnalytics, originTracing]);

	const handleClickLearnMore = useCallback(() => {
		fireAnalytics({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'lockScreenLearnMoreBenefitsLink',
			attributes: {
				...originTracing.toAnalyticsAttributes({ hasGeneratedId: true }),
			},
		});
	}, [fireAnalytics, originTracing]);

	const handleClickRemoveUsersAndSwitchToFree = useCallback(() => {
		fireAnalytics({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'manageUsers',
			attributes: {
				...originTracing.toAnalyticsAttributes({ hasGeneratedId: true }),
			},
		});
	}, [fireAnalytics, originTracing]);

	const handleClickSwitchToFree = useCallback(() => {
		fireAnalytics({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'downgradeToFree',
			attributes: {
				...originTracing.toAnalyticsAttributes({ hasGeneratedId: true }),
			},
		});
	}, [fireAnalytics, originTracing]);

	const handleClickAddPaymentDetails = useCallback(() => {
		fireAnalytics({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'addPaymentDetails',
			attributes: {
				...originTracing.toAnalyticsAttributes({ hasGeneratedId: true }),
			},
		});
	}, [fireAnalytics, originTracing]);

	const handleClickViewAdmins = useCallback(async () => {
		if (isDialogOpen) {
			setIsDialogOpen(false);
			return;
		}

		setIsLoading(true);
		fireAnalytics({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'showSiteAdmins',
		});

		try {
			const siteAdminsResponse = await fetchSiteAdmins(cloudId);
			const responses = await Promise.all(
				siteAdminsResponse.siteAdminIds.map((siteAdminId) => {
					return apolloClient.query({
						query: UserInfo,
						variables: {
							accountId: siteAdminId,
						},
					});
				}),
			);

			const admins = responses.map(
				({
					data: {
						user: { displayName, id },
					},
				}) => ({ displayName, id }),
			);

			setSiteAdmins(admins);
			setIsErrorFetchingAdmins(false);
		} catch (error: any) {
			setIsErrorFetchingAdmins(true);
			fireAnalytics({
				type: 'sendOperationalEvent',
				actionSubject: 'fetchingSiteAdmins',
				action: 'failed',
				attributes: {
					message: JSON.stringify(error),
				},
			});
		}

		setIsLoading(false);
		setIsDialogOpen(true);
	}, [isDialogOpen, cloudId, apolloClient, fireAnalytics]);

	useEffect(() => {
		fireAnalytics({
			type: 'sendScreenEvent',
			name: 'trialEndLockScreenScreen',
			attributes: {
				...originTracing.toAnalyticsAttributes({ hasGeneratedId: true }),
			},
		});
	}, [fireAnalytics, originTracing]);

	return (
		<ContentWrapper>
			<CustomHeader variation={usageOverLimit ? 'danger' : 'warning'} isDarkMode={isDarkMode}>
				<img src={isDarkMode ? spotHeroIllustrationDark : spotHeroIllustration} alt="" />
			</CustomHeader>
			<CustomBody>
				<ContentContainer>
					<BodyHeading id={titleId}>
						{usageOverLimit ? (
							<FormattedMessage {...i18n.aboveFreeUserLimitTitle} values={{ predunningDaysLeft }} />
						) : (
							<FormattedMessage {...contentModel.belowFreeUserLimitTitle} />
						)}
					</BodyHeading>
					{isAdmin && (
						<BodyText>
							{usageOverLimit ? (
								<FormattedMessage
									{...contentModel.adminAboveFreeUserLimitInstruction}
									values={{
										link: (msg: React.ReactNode[]) => (
											<BodyLink
												href={learnSwitchUrl}
												onClick={handleClickLearnSwitch}
												target="_blank"
												rel="noopener"
											>
												{msg}
											</BodyLink>
										),
										br: <br />,
									}}
								/>
							) : (
								<FormattedMessage {...contentModel.adminBelowFreeUserLimitInstruction} />
							)}
						</BodyText>
					)}
					<BodyListFrame>
						<BodyListTitle>
							{isAdmin ? (
								<FormattedMessage {...contentModel.billingAdminBenefitTitle} />
							) : (
								<FormattedMessage {...contentModel.nonBillingAdminBenefitTitle} />
							)}
						</BodyListTitle>
						<BodyListWrapper>
							{contentModel.benefits.map((benefit) => (
								<BodyListItem key={benefit.id}>
									<CheckCircleIcon
										size="small"
										primaryColor={token('color.icon.subtle', N400)}
										label=""
									/>
									<FormattedMessage {...benefit} />
								</BodyListItem>
							))}
						</BodyListWrapper>
						<BodyLink
							href={learnMoreUrl}
							onClick={handleClickLearnMore}
							target="_blank"
							rel="noopener"
						>
							<FormattedMessage {...contentModel.learnMoreUrlText} />
						</BodyLink>
					</BodyListFrame>
					{!isAdmin && (
						<BodyText>
							<FormattedMessage {...contentModel.contactBillingAdmin} />
						</BodyText>
					)}
				</ContentContainer>
				{isAdmin ? (
					<ButtonContainer>
						{isDismissible && (
							<Button appearance="subtle-link" onClick={onDismiss}>
								<FormattedMessage {...i18n.dismiss} />
							</Button>
						)}
						{usageOverLimit ? (
							<Button
								appearance="default"
								href={userManagementUrl}
								onClick={handleClickRemoveUsersAndSwitchToFree}
								target="_blank"
							>
								<FormattedMessage {...i18n.removeUsersAndSwitchToFree} />
							</Button>
						) : (
							<Button
								appearance="default"
								href={manageSubscriptionUrl}
								onClick={handleClickSwitchToFree}
								target="_blank"
							>
								<FormattedMessage {...i18n.switchToFree} />
							</Button>
						)}
						<Button
							appearance="primary"
							href={isBillingAdmin ? paymentDetailsUrl : manageSubscriptionUrl}
							onClick={handleClickAddPaymentDetails}
							target="_blank"
						>
							<FormattedMessage {...i18n.addPaymentDetails} />
						</Button>
					</ButtonContainer>
				) : (
					<SiteAdminSection>
						{isDismissible && (
							<Button appearance="subtle-link" onClick={onDismiss}>
								<FormattedMessage {...i18n.dismiss} />
							</Button>
						)}
						<InlineDialog
							content={
								<InlineDialogContentWrapper>
									<strong>
										<FormattedMessage {...i18n.siteAdmins} />
									</strong>
									{!isErrorFetchingAdmins && (
										<SiteAdminList>
											{siteAdmins.map(({ id, displayName }) => (
												<li key={id}>{displayName}</li>
											))}
										</SiteAdminList>
									)}
									{isErrorFetchingAdmins && <FormattedMessage {...i18n.failedToLoadSiteAdmins} />}
								</InlineDialogContentWrapper>
							}
							isOpen={isDialogOpen}
							placement="right"
							onClose={() => {
								setIsDialogOpen(false);
							}}
						>
							<LoadingButton
								isLoading={isLoading}
								appearance="primary"
								onClick={handleClickViewAdmins}
							>
								<FormattedMessage {...i18n.viewAdmins} />
							</LoadingButton>
						</InlineDialog>
					</SiteAdminSection>
				)}
			</CustomBody>
		</ContentWrapper>
	);
};

export const LockScreenModalContent = (
	props: Omit<LockScreenContentProps, 'isDismissible' | 'onDismiss'>,
) => {
	const { onClose, titleId } = useModal();

	return <LockScreenContent {...props} isDismissible onDismiss={onClose} titleId={titleId} />;
};
